// Resolves asset URLs and preloads shared/character assets.
// Seam #3 in docs/architecture.md — the only place FE knows filename patterns.
// Contract: docs/api-contract.md#asset-url-rule.

import type {
  CharacterAssets,
  SharedAssetMap,
  Theme,
} from "../state/types";
import { getConfig } from "../api/client";
import {
  backgroundName,
  cupCompositeUrl,
  HAZARD_INDICES,
  hazardName,
  INGREDIENTS_BY_THEME,
  ingredientName,
} from "./themeMap";
import { bodyFrameNames, handName } from "./genderMap";

export class AssetResolver {
  constructor(private apiBase: string) {}

  url(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    if (pathOrUrl.startsWith("/")) return `${this.apiBase}${pathOrUrl}`;
    return `${this.apiBase}/v1/shared/${pathOrUrl}`;
  }

  private sharedUrl(name: string): string {
    return `${this.apiBase}/v1/shared/${name}`;
  }

  async preloadShared(theme: Theme, audioCtx: AudioContext): Promise<SharedAssetMap> {
    const ingredientNames = INGREDIENTS_BY_THEME[theme].map(ingredientName);
    const hazardNames = HAZARD_INDICES.map(hazardName);

    const [
      background,
      cup,
      bodyFramesMale,
      bodyFramesFemale,
      handMale,
      handFemale,
      ingredients,
      hazards,
      particleIngredient,
      particleHazard,
      bgm,
      collectIngredient,
      collectHazard,
      endGame,
      config,
    ] = await Promise.all([
      this.loadImage(this.sharedUrl(backgroundName(theme))),
      // The held cup is the FE-static "_A" art (open cup), not the BE product shot.
      this.loadImage(cupCompositeUrl(theme)),
      Promise.all(bodyFrameNames(true, theme).map((n) => this.loadImage(this.sharedUrl(n)))),
      Promise.all(bodyFrameNames(false, theme).map((n) => this.loadImage(this.sharedUrl(n)))),
      // Foreground hands are optional art — a missing file degrades to no hand
      // layer rather than failing the whole preload.
      this.loadImage(this.sharedUrl(handName(true, theme))).catch(() => null),
      this.loadImage(this.sharedUrl(handName(false, theme))).catch(() => null),
      Promise.all(ingredientNames.map((n) => this.loadImage(this.sharedUrl(n)))),
      Promise.all(hazardNames.map((n) => this.loadImage(this.sharedUrl(n)))),
      this.loadImage(this.sharedUrl("particle_ingredient.png")),
      this.loadImage(this.sharedUrl("particle_hazard.png")),
      this.loadAudio(this.sharedUrl("bg_sound.mp3"), audioCtx),
      this.loadAudio(this.sharedUrl("collect_ingredient.mp3"), audioCtx),
      this.loadAudio(this.sharedUrl("collect_hazard.mp3"), audioCtx),
      this.loadAudio(this.sharedUrl("end_game.mp3"), audioCtx),
      getConfig(),
    ]);

    return {
      background,
      cup,
      bodyFramesMale,
      bodyFramesFemale,
      handMale,
      handFemale,
      ingredients,
      hazards,
      particleIngredient,
      particleHazard,
      audio: { bgm, collectIngredient, collectHazard, endGame },
      config,
    };
  }

  /** Decode a single composed head. Used when only a subset of emotions were
   *  captured (the rest fall back to default icons), so loadCharacter's all-3
   *  fetch would 404 on the missing ones. */
  async loadHead(urlOrPath: string): Promise<HTMLImageElement> {
    return this.loadImage(this.url(urlOrPath));
  }

  async loadCharacter(urls: { neutral: string; happy: string; sad: string }): Promise<CharacterAssets> {
    const [neutral, happy, sad] = await Promise.all([
      this.loadImage(this.url(urls.neutral)),
      this.loadImage(this.url(urls.happy)),
      this.loadImage(this.url(urls.sad)),
    ]);
    return { neutral, happy, sad };
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }

  private async loadAudio(src: string, ctx: AudioContext): Promise<AudioBuffer> {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Failed to load audio: ${src}`);
    const buf = await res.arrayBuffer();
    return ctx.decodeAudioData(buf);
  }
}
